<template>
  <div>
    <v-card class="pl-5 pr-5">
      <h1>LTI Platform</h1>
      <v-divider></v-divider>
      <Notification :notify="notify" />

      <div class="row" v-show="!isEdit">
        <div class="pl-3 col">
          <v-text-field
            id="searchLtiPlatform"
            v-model="Search"
            label="Search"
            required
            append-outer-icon="mdi-table-search"
            @click:append-outer="onSearch"
            v-on:keyup.enter="onSearch()"
          ></v-text-field>
        </div>
        <div class="col">
          <v-select
            id="orderBy"
            v-model="paging.isASC"
            :items="[{ type: 'Ascending', value: true }, { type: 'Descending', value: false }]"
            label="Order By"
            item-text="type"
            item-value="value"
            return-value="type"
            @change="getListByPagin()"
          ></v-select>
        </div>
        <div class="col"></div>
        <div class="col pl-2">
          <div class="float-right">
            <v-btn color="primary" @click="onNew()" class="mt-4" id="btnNew">
              New LTI Platform
            </v-btn>
          </div>
        </div>
      </div>

      <div v-show="isEdit">
        <v-form ref="form" v-model="valid" lazy-validation>
          <div class="row">
            <div class="col">
              <v-text-field
                id="platformName"
                label="Platform Name *"
                v-model="selectedObj.name"
                :rules="requiredRules"
                required
              ></v-text-field>
            </div>
            <div class="col">
              <v-text-field
                id="issuer"
                label="Platform URL *"
                v-model="selectedObj.issuer"
                :rules="requiredRules"
                required
              ></v-text-field>
            </div>
          </div>
          <div class="row">
            <div class="col">
              <v-text-field
                id="clientId"
                label="Client ID *"
                v-model="selectedObj.clientId"
                :rules="requiredRules"
                required
              ></v-text-field>
            </div>
            <div class="col">
              <v-text-field
                id="deploymentId"
                label="Deployment ID"
                v-model="selectedObj.deploymentId"
              ></v-text-field>
            </div>
          </div>
          <div class="row">
            <div class="col">
              <v-text-field
                id="authLoginUrl"
                label="Authentication Endpoint"
                v-model="selectedObj.authLoginUrl"
              ></v-text-field>
            </div>
            <div class="col">
              <v-text-field
                id="authTokenUrl"
                label="Access Token Endpoint"
                v-model="selectedObj.authTokenUrl"
              ></v-text-field>
            </div>
          </div>
          <div class="row">
            <div class="col">
              <v-text-field
                id="jwksUrl"
                label="JWKS URL"
                v-model="selectedObj.jwksUrl"
              ></v-text-field>
            </div>
            <div class="col">
              <v-select
                id="status"
                v-model="selectedObj.status"
                :items="['Active', 'Inactive']"
                label="Status"
              ></v-select>
            </div>
          </div>
          <div class="row">
            <div class="col">
              <v-text-field
                id="redirectUri"
                label="Redirect URI"
                v-model="selectedObj.redirectUri"
              ></v-text-field>
            </div>
            <div class="col">
              <v-text-field
                id="frontendUrl"
                label="Frontend URL"
                v-model="selectedObj.frontendUrl"
              ></v-text-field>
            </div>
          </div>
          <div class="row">
            <div class="col">
              <v-select
                id="organizationId"
                v-model="selectedObj.organizationId"
                :items="organizations"
                item-text="Name"
                item-value="id"
                label="Organization *"
                :rules="[(v) => !!v || 'Organization is required']"
                required
                @change="onOrganizationChange"
              ></v-select>
            </div>
            <div class="col">
              <v-select
                id="collectionId"
                v-model="selectedObj.collectionId"
                :items="collections"
                item-text="Name"
                item-value="id"
                label="Organization Collection"
                clearable
                :disabled="!selectedObj.organizationId"
              ></v-select>
            </div>
          </div>
          <div class="row" v-show="false">
            <div class="col">
              <v-textarea
                id="publicKey"
                label="Public Key"
                v-model="selectedObj.publicKey"
                rows="3"
                auto-grow
              ></v-textarea>
            </div>
          </div>
          <v-divider class="pt-2"></v-divider>
          <div class="row mb-4">
            <div class="col">
              <div class="float-right action-area">
                <v-btn @click="onCancel()" id="btnCancel">Cancel</v-btn>
                <v-btn
                  id="btnSave"
                  class="mr-4"
                  color="primary"
                  @click="onSave()"
                  :loading="loading"
                  :disabled="loading"
                >
                  Save
                </v-btn>
              </div>
            </div>
          </div>
        </v-form>
        <ConfirmationDialog ref="confirmEdit" />
      </div>

      <div class="row mt-5" v-show="!isEdit">
        <div class="col">
          <ConfirmationDialog ref="confirm" />
          <v-simple-table>
            <template v-slot:default>
              <thead>
                <tr>
                  <th class="text-left" style="cursor: pointer;" @click="sortBy('name')">Platform Name</th>
                  <th class="text-left" style="cursor: pointer;" @click="sortBy('issuer')">Platform URL</th>
                  <th class="text-left" style="cursor: pointer;" @click="sortBy('clientId')">Client ID</th>
                  <th class="text-left" style="cursor: pointer;" @click="sortBy('deploymentId')">Deployment ID</th>
                  <th class="text-left" style="cursor: pointer;" @click="sortBy('status')">Status</th>
                  <th style="width: 100px"></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in list" :key="item.id">
                  <td>{{ item.name }}</td>
                  <td>{{ item.issuer }}</td>
                  <td>{{ item.clientId }}</td>
                  <td>{{ item.deploymentId }}</td>
                  <td>
                    <v-chip
                      small
                      :color="item.status === 'Active' ? 'success' : 'default'"
                      :text-color="item.status === 'Active' ? 'white' : ''"
                    >{{ item.status }}</v-chip>
                  </td>
                  <td>
                    <v-icon class="mr-2 icon-edit" @click="onEdit(item)">mdi-comment-edit</v-icon>
                    <v-icon class="mr-2 icon-delete" @click="onDelete(item)">mdi-delete</v-icon>
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </div>
      </div>

      <div class="row" v-show="!isEdit">
        <div class="col">
          <Pagination :paging="paging" v-on:next="onNextPage" />
        </div>
      </div>
    </v-card>
  </div>
</template>

<script>
import Notification from "@/components/Notification";
import ConfirmationDialog from "@/components/ConfirmationDialog";
import Pagination from "@/components/Pagination";
import * as api from "@/api";

export default {
  name: "LtiPlatform",
  components: {
    Notification,
    ConfirmationDialog,
    Pagination,
  },
  created() {
    this.getListByPagin();
    this.loadOrganizations();
  },
  methods: {
    onSearch() {
      this.paging.page = 1;
      this.paging.keyword = this.Search;
      this.getListByPagin();
    },
    onNextPage(page) {
      this.paging.page = page;
      this.paging.keyword = this.Search;
      this.getListByPagin();
    },
    getListByPagin() {
      let that = this;
      this.list = [];
      new Promise((resolve, reject) => {
        api
          .getByPagin("lti/platforms", this.paging)
          .then((response) => {
            this.list = response.data.data;
            this.paging.count = response.data.count;
          })
          .catch((error) => {
            reject(error);
          });
      });
    },
    loadOrganizations() {
      api.getAll("organization").then((r) => {
        this.organizations = r.data || [];
      });
    },
    onOrganizationChange(orgId) {
      this.selectedObj.collectionId = null;
      this.collections = [];
      if (!orgId) return;
      api.getAll(`organization-collection/organization/${orgId}`).then((r) => {
        this.collections = [{ id: null, Name: "All" }, ...(r.data || [])];
      });
    },
    onNew() {
      this.isEdit = true;
      this.collections = [];
      this.selectedObj = {
        id: 0,
        name: "",
        issuer: "",
        clientId: "",
        deploymentId: "",
        authLoginUrl: "",
        authTokenUrl: "",
        jwksUrl: "",
        publicKey: "",
        redirectUri: "",
        frontendUrl: "",
        organizationId: null,
        collectionId: null,
        status: "Active",
      };
      this.valid = false;
    },
    onSave() {
      let that = this;
      this.$refs.form.validate();
      if (!this.valid) return;
      this.loading = true;
      let savedId = this.selectedObj.id;
      new Promise((resolve, reject) => {
        api
          .update("lti/platforms", this.selectedObj)
          .then((response) => {
            resolve(response);
            if (response.status === 200 || response.status === 201) {
              this.notify.type = 1;
              this.notify.message = savedId === 0
                ? "Record successfully created."
                : "Record successfully updated.";
              this.isEdit = false;
              this.$refs.form.reset();
              this.getListByPagin();
              setTimeout(() => { this.notify.type = 0; }, 3000);
            } else {
              this.notify.type = 4;
              this.notify.errors.push("Failed to save data.");
            }
          })
          .catch((error) => {
            this.notify.type = 4;
            this.notify.errors.push("Failed to save data. " + error);
            reject(error);
          })
          .finally(() => {
            this.loading = false;
          });
      });
    },
    onEdit(obj) {
      this.isEdit = true;
      this.collections = [];
      this.selectedObj = Object.assign({}, obj);
      if (obj.organizationId) {
        api.getAll(`organization-collection/organization/${obj.organizationId}`).then((r) => {
          this.collections = [{ id: null, Name: "All" }, ...(r.data || [])];
        });
      }
    },
    async onDelete(obj) {
      if (
        await this.$refs.confirm.open(
          "Confirm",
          "Are you sure you want to delete this LTI Platform?"
        )
      ) {
        new Promise((resolve, reject) => {
          api
            .DeleteByID("lti/platforms", obj.id)
            .then((response) => {
              resolve(response);
              if (response.status === 200 || response.status === 201) {
                this.notify.type = 1;
                this.notify.message = "Record successfully deleted.";
                setTimeout(() => {
                  this.notify.type = 0;
                  this.getListByPagin();
                }, 3000);
              } else {
                this.notify.type = 4;
                this.notify.errors.push("Failed to delete record.");
              }
            })
            .catch((error) => {
              this.notify.type = 4;
              this.notify.errors.push("Failed to delete record. " + error);
              reject(error);
            });
        });
      }
    },
    onCancel() {
      this.isEdit = false;
      this.$refs.form.reset();
    },
    sortBy(field) {
      if (this.sortField === field) {
        this.sortAsc = !this.sortAsc;
      } else {
        this.sortField = field;
        this.sortAsc = true;
      }
      this.list = this.list.slice().sort((a, b) => {
        const valA = a[field] != null ? String(a[field]) : "";
        const valB = b[field] != null ? String(b[field]) : "";
        return this.sortAsc ? valA.localeCompare(valB) : valB.localeCompare(valA);
      });
    },
  },
  data: () => ({
    list: [],
    isEdit: false,
    Search: "",
    organizations: [],
    collections: [],
    selectedObj: {
      id: 0,
      name: "",
      issuer: "",
      clientId: "",
      deploymentId: "",
      authLoginUrl: "",
      authTokenUrl: "",
      jwksUrl: "",
      publicKey: "",
      redirectUri: "",
      frontendUrl: "",
      organizationId: null,
      collectionId: null,
      status: "Active",
    },
    notify: {
      type: 0,
      message: "",
      errors: [],
    },
    valid: false,
    loading: false,
    requiredRules: [(v) => !!v || "This field is required"],
    paging: { count: 10, page: 1, size: 10, keyword: "", isASC: true },
    sortField: "",
    sortAsc: true,
  }),
};
</script>



// WEBPACK FOOTER //
// src/views/LtiPlatform.vue